/* eslint-disable @typescript-eslint/no-unused-vars */
import ButtonBase from '@material-ui/core/ButtonBase'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import ChevronLeft from '@material-ui/icons/ChevronLeftRounded'
import ChevronRight from '@material-ui/icons/ChevronRightRounded'
import moment from 'moment'
import Moment from 'moment'
import MomentJalaali from 'moment-jalaali'
import momentJalaali from 'moment-jalaali'
import React, { useEffect, useRef, useState } from 'react'

import {
  gregorianToJalaali,
  jalaaliToGregorian,
  DatePickerType,
  DateRangeType,
  getTimeView
} from '../../../../helpers/date'
import { useWindowDimensions } from '../../../../helpers/pageSize'
import { getDirection, lang } from '../../../../localization'
import {
  CustomDayPickerRangeControllerProps,
  MonthDetailsType
} from '../types/DateRangePicker.types'
import CustomMonthView from './CustomMonthView'
import CustomMonthViewMultiDates from './CustomMonthViewMultiDates'
import useStyles from './styles/customDayPickerRangeController.style'

const buildNewJalaaliMonth = (month: moment.Moment) => {
  const tempMonth = momentJalaali(month.clone())
  const monthLength = momentJalaali.jDaysInMonth(
    tempMonth.jYear(),
    tempMonth.jMonth()
  )
  const monthName = tempMonth.format('jMMMM')
  let numberOfEmptyDays = tempMonth.day()
  numberOfEmptyDays = numberOfEmptyDays === 6 ? 0 : numberOfEmptyDays + 1
  const newMonth = {
    firstDayDate: tempMonth,
    firstDay: tempMonth.format('jYYYY-jMM-jDD'),
    name: monthName,
    data: new Array(numberOfEmptyDays).fill(null)
  }
  for (let i = 1; i <= monthLength; i += 1) {
    newMonth.data.push(i)
  }

  return newMonth
}

const buildNewGregorianMonth = (month: moment.Moment): MonthDetailsType => {
  const tempMonth = month.clone()

  const monthLength = tempMonth.daysInMonth()
  const monthName = tempMonth.format('MMMM')

  const numberOfEmptyDays = tempMonth.day()

  const newMonth = {
    firstDayDate: tempMonth,
    firstDay: tempMonth.format('YYYY-MM-DD'),
    name: monthName,
    data: new Array(numberOfEmptyDays).fill(null)
  }

  for (let i = 1; i <= monthLength; i += 1) {
    newMonth.data.push(i)
  }

  return newMonth
}

const buildNewMonth = (
  month: moment.Moment,
  type: DatePickerType
): MonthDetailsType =>
  type === 'jalaali'
    ? buildNewJalaaliMonth(month)
    : buildNewGregorianMonth(month)

const CustomDayPickerRangeController: React.FC<
  CustomDayPickerRangeControllerProps
> = ({
  startDate,
  endDate,
  fixedStart,
  fixedEnd,
  numberOfVisibleMonths,
  showJalaaliHolidays,
  enablePastDays,
  enableAllDays,
  onChange,
  size,
  type,
  numberOfHoveredItems,
  autoSelectedEndDate,
  range,
  multipleDates,
  dateObjectList = [],
  goToday,
  endDateCanBeSelectAsStartDate,
  disableBefore,
  disableAfter,
  maxRange,
  selectingStart,
  setSelectingStart
}) => {
  const [newdisableAfter, setnewDisableAfter] = useState<string | undefined>(
    disableAfter
  )
  const [renderedMonths, setrenderedMonths] = useState<MonthDetailsType[]>([])
  const [goToTodayDetected, setGoToTodayDetected] = useState<boolean>(false)

  const todayDay =
    type === 'jalaali'
      ? parseInt(MomentJalaali().format(getTimeView(type).dayView), 10)
      : parseInt(Moment().format(getTimeView(type).dayView), 10)

  const [leftValue, setleftValue] = useState<number>(-375)
  const [hoveredDate, sethoveredDate] = useState<string | null>(null)
  const [lastSelectedDateRangeType, setlastSelectedDateRangeType] =
    useState<DateRangeType>('END_DATE')
  const classes = useStyles()

  const { width } = useWindowDimensions()

  const renderGregorianMonths = () => {
    let calendarStartDate: moment.Moment

    if (width < 600) {
      // Always start from current month in mobile
      calendarStartDate = enablePastDays
        ? moment().subtract(1, 'year').add(1, 'month')
        : moment().clone()
    } else {
      // Desktop uses startDate or today
      calendarStartDate = startDate
        ? moment(startDate).clone()
        : moment().clone()
    }

    const newRenderedMonths: MonthDetailsType[] = []
    const thisMonth =
      width < 600
        ? calendarStartDate.startOf('month')
        : calendarStartDate.subtract(1, 'months').startOf('month')

    const defaultNumberOfMonths = width < 600 ? 12 : 4
    for (let j = 0; j < defaultNumberOfMonths; j += 1) {
      const newMonth = buildNewMonth(thisMonth, type)
      newRenderedMonths.push(newMonth)
      if (j !== defaultNumberOfMonths - 1) {
        thisMonth.add(1, 'months')
      }
    }

    setrenderedMonths(newRenderedMonths)
  }

  const renderJalaaliMonths = () => {
    let calendarStartDate: moment.Moment

    if (width < 600) {
      // Always start from current month in mobile
      calendarStartDate = enablePastDays
        ? momentJalaali().subtract(1, 'year').add(1, 'month')
        : momentJalaali().clone()
    } else {
      // Desktop uses startDate or today
      calendarStartDate = startDate
        ? momentJalaali(startDate, 'jYYYY-jMM-jDD').clone()
        : momentJalaali().clone()
    }

    const newRenderedMonths: MonthDetailsType[] = []
    const thisMonth =
      width < 600
        ? calendarStartDate.startOf('jMonth')
        : calendarStartDate.subtract(1, 'jMonth').startOf('jMonth')

    const defaultNumberOfMonths = width < 600 ? 12 : 4
    for (let j = 0; j < defaultNumberOfMonths; j += 1) {
      const newMonth = buildNewMonth(thisMonth, type)
      newRenderedMonths.push(newMonth)
      if (j !== defaultNumberOfMonths - 1) {
        thisMonth.add(1, 'jMonth')
      }
    }

    setrenderedMonths(newRenderedMonths)
  }

  const renderMonths =
    type === 'jalaali' ? renderJalaaliMonths : renderGregorianMonths

  // Ref to the months list (rendered below). Used instead of document.querySelector so the
  // scroll-to-selected-month works inside a shadow root too — see ADR-034 H3.
  const monthsListRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (width >= 600 || !startDate || renderedMonths.length === 0) return

    // Find the month that matches the selected start date
    const monthIndex = renderedMonths.findIndex((m) => {
      const monthKey = m.firstDay.split('-').slice(0, 2).join('-')
      const startKey = startDate.split('-').slice(0, 2).join('-')
      return monthKey === startKey
    })

    if (monthIndex !== -1) {
      // A ref, not document.querySelector: this component also renders inside a shadow root
      // (the embed), and document.querySelector cannot pierce a shadow boundary — it would
      // return null and the scroll would silently never happen. The ref works in both.
      // See ADR-034 H3.
      const container = monthsListRef.current
      const monthElement = container?.children?.[monthIndex] as HTMLElement

      if (monthElement) {
        monthElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [startDate, renderedMonths, width])

  const onSelectEndDate = (
    selectedMonth: MonthDetailsType,
    selectedDay: number,
    selectedStartDate?: string
  ) => {
    const newStartDate = selectedStartDate || startDate

    if (!selectedDay) {
      onChange({
        gregorianStartDate:
          type === 'gregorian'
            ? newStartDate
            : jalaaliToGregorian(newStartDate || ''),
        gregorianEndDate: undefined,
        jalaaliStartDate:
          type === 'jalaali'
            ? newStartDate
            : gregorianToJalaali(newStartDate || ''),
        jalaaliEndDate: undefined
      })
      return
    }
    const SelectedDate =
      selectedMonth.firstDay.slice(0, 8) +
      (selectedDay < 10 ? '0' : '') +
      selectedDay

    if (
      newStartDate &&
      !endDateCanBeSelectAsStartDate &&
      SelectedDate <= newStartDate
    ) {
      return
    }

    onChange({
      gregorianStartDate:
        type === 'gregorian'
          ? newStartDate
          : jalaaliToGregorian(newStartDate || ''),
      gregorianEndDate:
        type === 'gregorian' ? SelectedDate : jalaaliToGregorian(SelectedDate),
      jalaaliStartDate:
        type === 'jalaali'
          ? newStartDate
          : gregorianToJalaali(newStartDate || ''),
      jalaaliEndDate:
        type === 'jalaali' ? SelectedDate : gregorianToJalaali(SelectedDate)
    })
    setlastSelectedDateRangeType('END_DATE')
    // setSelectingStart(true)
  }

  const handleAutoSelectEndDate = (
    selectedMonth: MonthDetailsType,
    selectedDay: number,
    SelectedDate: string
  ) => {
    if (
      selectedDay + numberOfHoveredItems! - 1 <=
      selectedMonth.data.filter((item) => !!item).length
    ) {
      onSelectEndDate(
        selectedMonth,
        selectedDay + numberOfHoveredItems! - 1,
        SelectedDate
      )
    } else {
      const startDateRenderedMonthIndex = renderedMonths.findIndex(
        (item) => item.firstDay === selectedMonth.firstDay
      )
      const calcSelectedDay =
        type === 'jalaali'
          ? parseInt(
              momentJalaali(SelectedDate, 'jYYYY/jM/jD')
                .clone()
                .add(numberOfHoveredItems! - 1, 'day')
                .format('jD'),
              10
            )
          : parseInt(
              moment(SelectedDate, 'YYYY-MM-DD')
                .clone()
                .add(numberOfHoveredItems! - 1, 'days')
                .format('D'),
              10
            )

      onSelectEndDate(
        renderedMonths[startDateRenderedMonthIndex + 1],
        calcSelectedDay,
        SelectedDate
      )
    }
  }

  const onSelectStartDate = (
    selectedMonth: MonthDetailsType,
    selectedDay: number,
    notEmptyEndDate: boolean
  ) => {
    const SelectedDate =
      selectedMonth.firstDay.slice(0, 8) +
      (selectedDay < 10 ? '0' : '') +
      selectedDay

    // Calculate the new disableAfter only if maxRange is defined and greater than zero
    let newDisableAfter
    if (maxRange && maxRange > 0) {
      newDisableAfter =
        type === 'gregorian'
          ? moment(SelectedDate).add(maxRange, 'days').format('YYYY-MM-DD')
          : momentJalaali(SelectedDate, 'jYYYY-jMM-jDD')
              .add(maxRange, 'days')
              .format('jYYYY-jMM-jDD')
    }

    if (notEmptyEndDate && endDate) {
      onChange({
        gregorianStartDate:
          type === 'gregorian'
            ? SelectedDate
            : jalaaliToGregorian(SelectedDate),
        gregorianEndDate:
          type === 'gregorian' ? endDate : jalaaliToGregorian(endDate),
        jalaaliStartDate:
          type === 'jalaali' ? SelectedDate : gregorianToJalaali(SelectedDate),
        jalaaliEndDate:
          type === 'gregorian' ? endDate : jalaaliToGregorian(endDate)
      })
    } else {
      onChange({
        gregorianStartDate:
          type === 'gregorian'
            ? SelectedDate
            : jalaaliToGregorian(SelectedDate),
        gregorianEndDate: undefined,
        jalaaliStartDate:
          type === 'jalaali' ? SelectedDate : gregorianToJalaali(SelectedDate),
        jalaaliEndDate: undefined
      })
    }
    setlastSelectedDateRangeType('START_DATE')

    if (newDisableAfter) {
      setnewDisableAfter(newDisableAfter) // Store the calculated value only if maxRange exists
    }

    if (autoSelectedEndDate && numberOfHoveredItems) {
      handleAutoSelectEndDate(selectedMonth, selectedDay, SelectedDate)
    }
    // setSelectingStart(false)
  }

  const onHoverDate = (
    selectedMonth: MonthDetailsType,
    selectedDay: number
  ) => {
    const SelectedDate =
      selectedMonth.firstDay.slice(0, 8) +
      (selectedDay < 10 ? '0' : '') +
      selectedDay
    sethoveredDate(SelectedDate)
  }

  const shiftBackward = () => {
    const newRenderedMonths = renderedMonths.slice(0, 3)
    const firstMonth = newRenderedMonths[0].firstDayDate.clone()
    const newMonth = buildNewMonth(
      type === 'jalaali'
        ? firstMonth.subtract(1, 'jMonth')
        : firstMonth.subtract(1, 'months'),
      type
    )
    newRenderedMonths.unshift(newMonth)
    setleftValue(-375)
    setrenderedMonths(newRenderedMonths)
  }

  const shiftForward = () => {
    const newRenderedMonths = renderedMonths.slice(1)
    const thirdMonth = newRenderedMonths[2].firstDayDate.clone()
    const newMonth = buildNewMonth(
      type === 'jalaali'
        ? thirdMonth.add(1, 'jMonth')
        : thirdMonth.add(1, 'months'),
      type
    )
    newRenderedMonths.push(newMonth)
    setleftValue(-375)
    setrenderedMonths(newRenderedMonths)
  }

  const onPrevMonthClicked = () => {
    setleftValue((currentLeftValue: number) => currentLeftValue + 375)
    setTimeout(() => shiftBackward(), 100)
  }

  const onNextMonthClicked = () => {
    setleftValue((currentLeftValue: number) => currentLeftValue - 375)

    setTimeout(() => {
      shiftForward()
    }, 100)
  }

  useEffect(() => {
    renderMonths()
    setGoToTodayDetected(true)
  }, [goToday])

  const selectedStartDate =
    lang === 'en'
      ? startDate
        ? moment(startDate).format('YYYY-MM-DD')
        : null
      : startDate
      ? startDate
      : null

  const selectedEndDate =
    lang === 'en'
      ? endDate
        ? moment(endDate).format('YYYY-MM-DD')
        : null
      : endDate
      ? endDate
      : null

  return (
    <Grid className={classes.rootGrid}>
      <Grid
        container
        ref={monthsListRef}
        className={`${classes.monthsListContainer} months-list-container`}
        style={{
          left:
            getDirection() === 'ltr'
              ? width < 600
                ? 'unset'
                : width < 864
                ? leftValue - 20
                : leftValue
              : 'unset',
          right:
            getDirection() === 'rtl'
              ? width < 600
                ? 'unset'
                : width < 864
                ? leftValue - 20
                : leftValue
              : 'unset',
          transition: leftValue === -375 ? 'none' : 'all 0.1s'
        }}>
        {renderedMonths.map((singleMonth, index) => (
          <Grid
            data-cy={index == 2 ? 'datePickerMonthContainer' : null}
            item
            key={singleMonth.name}
            className={[
              index === renderedMonths.length - 1
                ? classes.datePickerLastMonth
                : ''
            ].join(' ')}>
            <CustomMonthView
              numberOfVisibleMonths={numberOfVisibleMonths}
              data={singleMonth.data}
              name={singleMonth.name}
              currentMonth={singleMonth.firstDay.split('-')[1]}
              currentYear={singleMonth.firstDay.split('-')[0]}
              currentMonthFirstDay={singleMonth.firstDay}
              selectedStartDate={selectedStartDate}
              selectedEndDate={selectedEndDate}
              hoveredDate={hoveredDate}
              selectFromPast={enablePastDays}
              selectAllDays={enableAllDays}
              onSelectStartDate={(selectedDay: number) => {
                const SelectedDate =
                  singleMonth.firstDay.slice(0, 8) +
                  (selectedDay < 10 ? '0' : '') +
                  selectedDay

                if (selectingStart) {
                  if (endDate) {
                    if (SelectedDate > endDate) {
                      onSelectStartDate(singleMonth, selectedDay, false)
                    } else if (SelectedDate == startDate) {
                      onSelectStartDate(singleMonth, selectedDay, true)
                      setSelectingStart(false)
                    } else if (SelectedDate < endDate) {
                      onSelectStartDate(singleMonth, selectedDay, true)
                    }
                  } else if (startDate && SelectedDate > startDate) {
                    onSelectEndDate(singleMonth, selectedDay)
                  } else if (startDate && SelectedDate < startDate) {
                    onSelectStartDate(singleMonth, selectedDay, false)
                    setSelectingStart(false)
                  }
                } else {
                  if (startDate) {
                    if (SelectedDate > startDate) {
                      onSelectEndDate(singleMonth, selectedDay)
                      // setSelectingStart(true)
                    } else if (SelectedDate < startDate) {
                      onSelectStartDate(singleMonth, selectedDay, false)
                    }
                  } else {
                    onSelectStartDate(singleMonth, selectedDay, false)
                  }
                }
              }}
              onSelectEndDate={(selectedDay: number) => {
                const SelectedDate =
                  singleMonth.firstDay.slice(0, 8) +
                  (selectedDay < 10 ? '0' : '') +
                  selectedDay

                if (selectingStart) {
                  if (endDate) {
                    if (SelectedDate > endDate) {
                      onSelectStartDate(singleMonth, selectedDay, false)
                    } else if (SelectedDate < endDate) {
                      onSelectStartDate(singleMonth, selectedDay, true)
                    }
                  } else if (startDate && SelectedDate > startDate) {
                    onSelectEndDate(singleMonth, selectedDay)

                    setSelectingStart(false)
                  } else if (startDate && SelectedDate < startDate)
                    onSelectStartDate(singleMonth, selectedDay, false)
                } else {
                  if (startDate) {
                    if (SelectedDate > startDate) {
                      onSelectEndDate(singleMonth, selectedDay)

                      // setSelectingStart(true)
                    } else if (SelectedDate < startDate) {
                      onSelectStartDate(singleMonth, selectedDay, false)
                    }
                  } else {
                    onSelectStartDate(singleMonth, selectedDay, false)
                  }
                }
              }}
              onHoverDate={(selectedDay: number) =>
                onHoverDate(singleMonth, selectedDay)
              }
              lastSelectedDateRangeType={lastSelectedDateRangeType}
              type={type}
              showJalaaliHolidays
              range={range}
              multipleDates={multipleDates}
              selectedDatesList={dateObjectList
                .filter(
                  (selectedDateItem) =>
                    selectedDateItem.gregorianStartDate?.split('-')[1] ===
                    singleMonth.firstDay!.split('-')[1]
                )
                .map((item) =>
                  parseInt(item.gregorianStartDate?.split('-')[2] || '0', 10)
                )}
              todayDay={todayDay}
              endDateCanBeSelectAsStartDate={endDateCanBeSelectAsStartDate}
              disableBefore={disableBefore}
              disableAfter={disableAfter || newdisableAfter}
            />
          </Grid>
        ))}
      </Grid>
      <Hidden xsDown>
        <ButtonBase
          className={classes.prevMonthButton}
          onClick={onPrevMonthClicked}>
          <ChevronLeft className={classes.prevMonthIcon} />
        </ButtonBase>
      </Hidden>
      <Hidden xsDown>
        <ButtonBase
          className={classes.nextMonthButton}
          onClick={onNextMonthClicked}>
          <ChevronRight className={classes.nextMonthIcon} />
        </ButtonBase>
      </Hidden>
    </Grid>
  )
}

export default CustomDayPickerRangeController
